import { createSelector } from '@reduxjs/toolkit';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'preact/compat';
import { useCallback, useEffect } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { postUserAppOnboardedMutationFn } from '@/queries/users';
import { AppState, useSelector, useStore } from '@/redux/helper';
import { createSortedHabitatsSelector } from '@/redux/selectors/habitats';
import { selectActiveProductNames, selectHasExplorerFeature } from '@/redux/selectors/users';
import { updateUserProperty } from '@/redux/slices/user/actions';
import { trackMP } from '@/shared/mp';
import { goToPostAuthDestination, isPostAuthDestination } from '@/shared/postAuthDestination';
import { getMostRecentOnlineVisitedHabitat } from '@/shared/visitedHabitats';

import ExplorerDayPass from '../products/ExplorerDayPass';
import SingleHabitatDayPass from '../products/SingleHabitatDayPass';

const selectDefaultHabitat = createSelector(
  selectHasExplorerFeature,
  createSortedHabitatsSelector(['online', 'lastMediaCapture', 'index', 'currentViewers', 'title']),
  (hasExplorerFeature, habitats) => (hasExplorerFeature ? undefined : habitats?.[0]),
);

// Memoized on allHabitats (the heavy input) so the store.subscribe loop below — which fires on
// every Redux dispatch in this realtime app — doesn't rebuild the online-habitats Map each time.
const selectInitialHabitat = createSelector(
  (state: AppState) => state.allHabitats,
  selectDefaultHabitat,
  (allHabitats, defaultHabitat) => getMostRecentOnlineVisitedHabitat({ allHabitats } as AppState) ?? defaultHabitat,
);

const AppOnboardingProductsOverview = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const isEducator = useSelector((state) => state.user.persona === 'educator');
  const hasExplorerFeature = useSelector(selectHasExplorerFeature);
  const hasActiveL1L2Plan = useSelector((state) =>
    Object.values(state.user.subscription.habitatsAccessUntil ?? {}).some((value) => Date.parse(value) > Date.now()),
  );
  const [habitat, setHabitat] = useState(() => selectInitialHabitat(store.getState()));
  const shouldSkipOnboarding = hasExplorerFeature || hasActiveL1L2Plan || isPostAuthDestination('/redeem');

  const { mutate: markOnboarded } = useMutation({
    mutationFn: postUserAppOnboardedMutationFn,
    onSuccess: (data) => {
      dispatch(updateUserProperty(data));
      goToPostAuthDestination();
    },
  });

  const finishOnboarding = useCallback(() => {
    markOnboarded();
    trackMP('after-signup-step', {
      step: 'productsOverview',
      activeProductNames: selectActiveProductNames(store.getState()),
    });
  }, [markOnboarded, store]);

  // only update the cached habitats if there are no habitats in the cache
  useEffect(() => {
    if (habitat?._id || hasExplorerFeature) {
      return undefined;
    }
    const unsubscribe = store.subscribe(() => {
      const nextHabitat = selectInitialHabitat(store.getState());
      if (nextHabitat?._id) {
        setHabitat(nextHabitat);
      }
    });
    return () => unsubscribe();
  }, [store, habitat?._id, hasExplorerFeature]);

  // skip onboarding if user has explorer feature, active L1/L2 plan, or destination is /redeem
  useEffect(() => {
    if (shouldSkipOnboarding) {
      finishOnboarding();
    }
  }, [shouldSkipOnboarding, finishOnboarding]);

  if (shouldSkipOnboarding) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0">
        <h1 className="preflight preflight-h1 p-5 pb-4 pt-10 text-center text-2xl/7 font-bold text-[#162B1B]">
          How Do You Want to Visit?
        </h1>
      </div>
      <div className="min-h-0 grow overflow-y-auto px-8 pb-8">
        <div className="flex flex-col items-stretch justify-center gap-8 pt-4">
          <ExplorerDayPass variant="primary" forEducator={isEducator} entryPoint="onboarding" />
          {!hasExplorerFeature && habitat && <SingleHabitatDayPass habitatId={habitat._id} entryPoint="onboarding" />}
          <button
            type="button"
            className="preflight preflight-button text-grey-6 underline"
            onClick={() => finishOnboarding()}
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppOnboardingProductsOverview;
